// The browser's position, sent with chat messages when this device opted in (Settings → Location).
// Stored per device, because the browser's permission is per device.
const KEY = 'aveq:use-current-location';
const MAX_AGE_MS = 10 * 60 * 1000;

let cached = null;  // { lat, lng, at }

// ~1 km: enough for weather, without pinpointing a home. Every helper the user talks to sees it.
const round = (n) => Math.round(n * 100) / 100;

export const geolocationSupported = () =>
  typeof navigator !== 'undefined' && Boolean(navigator.geolocation);

export function currentLocationEnabled() {
  try { return window.localStorage.getItem(KEY) === '1'; } catch (e) { return false; }
}

function remember(on) {
  try {
    if (on) window.localStorage.setItem(KEY, '1');
    else window.localStorage.removeItem(KEY);
  } catch (e) { /* storage blocked: stays off */ }
}

// Ask the browser. Resolves to { lat, lng }, or null when denied, unavailable or timed out.
export function refreshCurrentLocation() {
  if (!geolocationSupported()) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (p) => {
        cached = { lat: round(p.coords.latitude), lng: round(p.coords.longitude), at: Date.now() };
        resolve({ lat: cached.lat, lng: cached.lng });
      },
      () => { cached = null; resolve(null); },
      { maximumAge: MAX_AGE_MS, timeout: 10000 });
  });
}

// Turning it on asks the browser and stays off if it won't share. Resolves to whether it is on.
export async function setCurrentLocationEnabled(on) {
  if (!on) {
    remember(false);
    cached = null;
    return false;
  }
  const coords = await refreshCurrentLocation();
  remember(Boolean(coords));
  return Boolean(coords);
}

// The position to send now: the last one known, refreshed in the background when stale.
// Null when off or not known yet, so the saved Location is used instead.
export function currentCoords() {
  if (!currentLocationEnabled()) return null;
  if (!cached || Date.now() - cached.at > MAX_AGE_MS) refreshCurrentLocation();
  return cached && { lat: cached.lat, lng: cached.lng };
}
